import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  QueryId,
  TOTAL_PRICE_DEPENDENT_QUERIES,
} from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseMutationDeletePreBookingDiscountCodeData =
  Booking.Components['PutDiscountCodeResponse'];

interface UseMutationDeletePreBookingDiscountCodeError {
  errors: any;
  message?: string;
}

interface UseMutationDeletePreBookingDiscountCodeParameters {
  brand?: Content.Properties.Brand;
  discountCode: string;
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
}

const useMutationDeletePreBookingDiscountCode = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    UseMutationDeletePreBookingDiscountCodeData,
    UseMutationDeletePreBookingDiscountCodeError,
    UseMutationDeletePreBookingDiscountCodeParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/discount-codes/delete-discount-code',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            discountCode: parameters.discountCode,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'DELETE',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error(
            'bookingFlow.useMutationDeletePreBookingDiscountCode',
            error,
          );

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (_data, parameters) => {
        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });

        TOTAL_PRICE_DEPENDENT_QUERIES.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationDeletePreBookingDiscountCode;
